import {
  DirectionEnum,
  IDropdownItem,
  IPriceRateSearchCurrentRateInfo,
  IPriceRateSearchMatchedSummary,
  IPriceRateSearchRideContext,
  isValidPositiveNumber,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

export const PRICE_RATE_SEARCH_LOGS_REFRESH_DEBOUNCE_MS = 400;

const PRICE_RATE_SEARCH_TYPE_LABEL_KEYS = new Map<number, string>([
  [1, "geoDynamicRide"],
  [2, "courseFixed"],
  [3, "distanceKm"],
  [4, "timePeriodDailyHourly"],
]);

export const resolveInitialPriceRateSearchLock = (
  transferredId?: number | null,
): boolean => isValidPositiveNumber(transferredId);

export const resolvePriceRateSearchTypeLabelKey = (
  type?: number | null,
): string | null => {
  if (!isValidPositiveNumber(type)) {
    return null;
  }
  return PRICE_RATE_SEARCH_TYPE_LABEL_KEYS.get(type) ?? null;
};

export const resolvePriceRateSearchTitleFromDropdown = (
  dropdownLabel: string,
  id: number,
): string => {
  const trimmed = dropdownLabel.trim();
  if (!trimmed) {
    return "";
  }
  const idPrefix = `${id} `;
  if (trimmed.startsWith(idPrefix)) {
    return trimmed.slice(idPrefix.length).trim();
  }
  return trimmed;
};

const toDropdownOptionId = (value: IDropdownItem["value"]): number | null => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const findPriceRateSearchDropdownItem = (
  options: IDropdownItem[],
  id: number | null,
): IDropdownItem | undefined => {
  if (id === null) {
    return undefined;
  }
  return options.find(item => toDropdownOptionId(item.value) === id);
};

export const resolvePriceRateSearchDropdownItemLabel = (
  options: IDropdownItem[],
  id: number | null,
): string => {
  if (id === null) {
    return "";
  }
  return findPriceRateSearchDropdownItem(options, id)?.label ?? String(id);
};

export const formatPriceRateSearchSupplierPriority = (
  item: IDropdownItem | undefined,
): string => {
  const priority = item?.priority;
  if (typeof priority !== "number" || !Number.isFinite(priority)) {
    return NO_VALUE_DISPLAY;
  }
  return String(priority);
};

export const formatPriceRateSearchVehicleTypeOptionLabel = (
  options: IDropdownItem[],
  id: number | null,
): string => {
  if (id === null) {
    return "";
  }
  const name = resolvePriceRateSearchDropdownItemLabel(options, id);
  return `#${id} · ${name}`;
};

export const formatPriceRateSearchSupplierMetaLine = ({
  supplierId,
  supplierOptions,
  idLabel,
  priorityLabel,
}: {
  supplierId: number | null;
  supplierOptions: IDropdownItem[];
  idLabel: string;
  priorityLabel: string;
}): string | null => {
  if (supplierId === null) {
    return null;
  }
  const item = findPriceRateSearchDropdownItem(supplierOptions, supplierId);
  const priority = formatPriceRateSearchSupplierPriority(item);
  return `${idLabel}: ${supplierId} · ${priorityLabel}: ${priority}`;
};

export const formatPriceRateSearchVehicleTypeIdMetaLine = ({
  vehicleTypeId,
  idLabel,
}: {
  vehicleTypeId: number | null;
  idLabel: string;
}): string | null => {
  if (vehicleTypeId === null) {
    return null;
  }
  return `${idLabel}: ${vehicleTypeId}`;
};

export const formatPriceRateSearchVehicleTypeMetaLine = ({
  vehicleTypeId,
  idLabel,
  placesLine,
}: {
  vehicleTypeId: number | null;
  idLabel: string;
  placesLine: string | null;
}): string | null => {
  const parts: string[] = [];
  const idPart = formatPriceRateSearchVehicleTypeIdMetaLine({
    vehicleTypeId,
    idLabel,
  });
  if (idPart) {
    parts.push(idPart);
  }
  const trimmedPlaces = placesLine?.trim();
  if (trimmedPlaces) {
    parts.push(trimmedPlaces);
  }
  return parts.length ? parts.join(" · ") : null;
};

export const resolvePriceRateSearchVehicleTypeMaxPlaces = ({
  vehicleTypeId,
  vehicleTypeOptions,
  rideContext,
}: {
  vehicleTypeId: number | null;
  vehicleTypeOptions: IDropdownItem[];
  rideContext: IPriceRateSearchRideContext | null;
}): number | null => {
  if (vehicleTypeId === null) {
    return null;
  }
  const fromDropdown = vehicleTypeOptions.find(
    item => toDropdownOptionId(item.value) === vehicleTypeId,
  )?.maxPassengers;
  if (typeof fromDropdown === "number" && Number.isFinite(fromDropdown)) {
    return fromDropdown;
  }
  if (
    rideContext?.vehicleTypeId === vehicleTypeId &&
    typeof rideContext.vehicleTypeMaxPlaces === "number"
  ) {
    return rideContext.vehicleTypeMaxPlaces;
  }
  return null;
};

export const formatPriceRateSearchVehicleTypePlacesLine = ({
  vehicleTypeMaxPlaces,
  geoProdMaxPlaces,
  geoProdMaxPlacesB,
  rideDirection,
}: {
  vehicleTypeMaxPlaces: number | null;
  geoProdMaxPlaces: number | null;
  geoProdMaxPlacesB: number | null;
  rideDirection?: string | null;
}): string | null => {
  const showMaxPlacesB =
    rideDirection === DirectionEnum.DropOff &&
    typeof geoProdMaxPlacesB === "number" &&
    Number.isFinite(geoProdMaxPlacesB);
  if (
    vehicleTypeMaxPlaces === null &&
    geoProdMaxPlaces === null &&
    !showMaxPlacesB
  ) {
    return null;
  }
  const maxPlacesText =
    vehicleTypeMaxPlaces !== null
      ? String(vehicleTypeMaxPlaces)
      : NO_VALUE_DISPLAY;
  const geoText =
    geoProdMaxPlaces !== null ? String(geoProdMaxPlaces) : NO_VALUE_DISPLAY;
  let params = `geoProdMaxPlaces: ${geoText}`;
  if (showMaxPlacesB) {
    params += `, maxPlacesB: ${geoProdMaxPlacesB}`;
  }
  return `Max places: ${maxPlacesText} (${params})`;
};

export type TPriceRateSearchMatchStatus =
  "pending" | "current-is-winner" | "different-winner" | "not-found";

export const resolvePriceRateSearchMatchStatus = ({
  isSearchSettled,
  currentPriceRateId,
  matchedPriceRateId,
}: {
  isSearchSettled: boolean;
  currentPriceRateId?: number | null;
  matchedPriceRateId?: number | null;
}): TPriceRateSearchMatchStatus => {
  if (!isSearchSettled) {
    return "pending";
  }
  if (!isValidPositiveNumber(matchedPriceRateId)) {
    return "not-found";
  }
  if (
    isValidPositiveNumber(currentPriceRateId) &&
    currentPriceRateId === matchedPriceRateId
  ) {
    return "current-is-winner";
  }
  return "different-winner";
};

export const resolvePriceRateSearchRateTitle = ({
  name,
  label,
}: {
  name?: string | null;
  label?: string | null;
}): string | null => {
  const trimmedName = name?.trim();
  if (trimmedName) {
    return trimmedName;
  }
  const trimmedLabel = label?.trim();
  return trimmedLabel || null;
};

export const resolvePriceRateSearchMatchedRateInfo = (
  matched: IPriceRateSearchMatchedSummary | null | undefined,
): IPriceRateSearchCurrentRateInfo | null => {
  if (!matched) {
    return null;
  }
  if (matched.rate?.id) {
    return matched.rate;
  }
  return {
    id: matched.id,
    name: matched.name?.trim() || null,
    label: matched.name?.trim() || null,
    supplierId: null,
    supplierName: null,
    vehicleTypeId: null,
    vehicleTypeName: null,
    maxDuration: null,
    type: null,
    sourceAreaId: null,
    sourceAreaName: null,
    destinationAreaId: null,
    destinationAreaName: null,
    minPrice: matched.basePrice,
  };
};

export const buildStubPriceRateSearchCurrentRate = (
  priceRateId?: number | null,
): IPriceRateSearchCurrentRateInfo | null => {
  if (!isValidPositiveNumber(priceRateId)) {
    return null;
  }
  return {
    id: priceRateId,
    name: null,
    label: null,
    supplierId: null,
    supplierName: null,
    vehicleTypeId: null,
    vehicleTypeName: null,
    maxDuration: null,
    type: null,
    sourceAreaId: null,
    sourceAreaName: null,
    destinationAreaId: null,
    destinationAreaName: null,
    minPrice: null,
  };
};

const BASE_PRICE_ADDITION_LABEL = "basePrice";

export const resolvePriceRateSearchDisplayedPriceBreakdown = ({
  basePrice,
  additions,
  total,
}: {
  basePrice: number | null;
  additions: { label: string; value: number }[];
  total: number | null;
}): {
  basePrice: number | null;
  additions: { label: string; value: number }[];
  total: number | null;
} => {
  const extraAdditions = additions.filter(
    row => row.label !== BASE_PRICE_ADDITION_LABEL,
  );
  const baseFromAdditions = additions.find(
    row => row.label === BASE_PRICE_ADDITION_LABEL,
  );
  return {
    basePrice: basePrice ?? baseFromAdditions?.value ?? null,
    additions: extraAdditions,
    total,
  };
};

export const resolvePriceRateSearchResetTargets = ({
  currentPriceRate,
  rideSupplierId,
  rideVehicleTypeId,
}: {
  currentPriceRate: IPriceRateSearchCurrentRateInfo | null;
  rideSupplierId?: number | null;
  rideVehicleTypeId?: number | null;
}): { supplierId: number | null; vehicleTypeId: number | null } => {
  const rateSupplierId = currentPriceRate?.supplierId;
  const rateVehicleTypeId = currentPriceRate?.vehicleTypeId;
  const resolveId = (
    rateId: number | null | undefined,
    rideId: number | null | undefined,
  ): number | null => {
    if (isValidPositiveNumber(rateId)) {
      return rateId;
    }
    if (isValidPositiveNumber(rideId)) {
      return rideId;
    }
    return null;
  };
  return {
    supplierId: resolveId(rateSupplierId, rideSupplierId),
    vehicleTypeId: resolveId(rateVehicleTypeId, rideVehicleTypeId),
  };
};
